/**
 * Shared icon-button + action icon set for list-row actions.
 *
 * `IconButton` renders an accessible, tooltipped square button with a hover tint keyed by
 * `tone`, and a larger tap target on touch screens. The icons are inline SVGs
 * (stroke=currentColor) so they inherit the tone colour.
 * Used across the admin list screens so row actions look + behave consistently.
 */

import type { ReactNode } from "react";

type Tone = "default" | "brand" | "danger" | "success" | "warning";

// Tones map onto the semantic tokens in src/index.css — no literal colours here.
const TONE: Record<Tone, string> = {
  default: "text-ink-muted hover:bg-brand-50 hover:text-brand",
  brand: "text-brand hover:bg-brand/10",
  danger: "text-danger hover:bg-danger-soft",
  success: "text-success hover:bg-success-soft",
  warning: "text-warning hover:bg-warning-soft",
};

export function IconButton({
  label,
  onClick,
  tone = "default",
  disabled,
  children,
}: {
  label: string; // accessible name + tooltip
  onClick: () => void;
  tone?: Tone;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      // On a touch screen the tap target grows past the 32px it shows, to 36px: half of
      // RowActions' 4px gap on every side, so a tap between two buttons — beside, or above and
      // below where the actions wrap onto two lines — never lands on the far one. The layout
      // doesn't move: rows keep their heights, columns their widths (RESP-10).
      className={`relative grid h-8 w-8 place-items-center rounded-lg transition pointer-coarse:after:absolute pointer-coarse:after:-inset-0.5 disabled:opacity-40 ${TONE[tone]}`}
    >
      <span className="h-[18px] w-[18px]">{children}</span>
    </button>
  );
}

/** `className="flex-wrap"` lets the buttons wrap in a narrow table column on a phone. */
export function RowActions({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`flex items-center justify-end gap-1 ${className}`}>{children}</span>;
}

/* --- icons --- */

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-full w-full"
    >
      {children}
    </svg>
  );
}

export const EditIcon = () => (
  <Svg>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Svg>
);

export const TrashIcon = () => (
  <Svg>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M6 6l1 14h10l1-14" />
    <path d="M10 11v6M14 11v6" />
  </Svg>
);

export const SaveIcon = () => (
  <Svg>
    <path d="M5 3h11l3 3v15H5z" />
    <path d="M8 3v6h8" />
    <path d="M8 21v-6h8v6" />
  </Svg>
);

export const CheckIcon = () => (
  <Svg>
    <path d="M5 12l5 5L20 6" />
  </Svg>
);

export const PauseIcon = () => (
  <Svg>
    <path d="M8 5v14M16 5v14" />
  </Svg>
);

export const PlayIcon = () => (
  <Svg>
    <path d="M7 5l12 7-12 7z" />
  </Svg>
);

export const UnlockIcon = () => (
  <Svg>
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 7.5-2" />
  </Svg>
);

export const LockIcon = () => (
  <Svg>
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Svg>
);

export const KeyIcon = () => (
  <Svg>
    <circle cx="8" cy="15" r="4" />
    <path d="M11 12l9-9" />
    <path d="M17 6l3 3" />
    <path d="M15 8l2 2" />
  </Svg>
);

export const LinkIcon = () => (
  <Svg>
    <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
    <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
  </Svg>
);

export const ChecklistIcon = () => (
  <Svg>
    <path d="M3 6l2 2 3-3" />
    <path d="M3 14l2 2 3-3" />
    <path d="M11 6h10M11 14h10M11 20h6" />
  </Svg>
);
